import {
  getUserErrorMessage,
  serverErrorMessage,
} from '@app/pages/cart/errorDict';
import {
  AppliedCoupon,
  ApplyCouponHandler,
  AssignedCoupon,
  CouponError,
  RemoveCouponHandler,
  ShippingAddress,
} from '@app/types/cart/Coupon';
import Box from '@redbubble/design-system/src/Box';
import Button from '@redbubble/design-system/src/Button';
import * as constants from '@redbubble/design-system/src/constants';
import Input from '@redbubble/design-system/src/Form/Input';
import { defineMessages, useIntl } from '@redbubble/next/intl';
import React, { forwardRef, useCallback, useEffect, useState } from 'react';
import styles from '../Coupon.module.scss';
import { couponMessages } from '../messages';
import { CouponAppliedMessage } from './CouponAppliedMessage/CouponAppliedMessage';

export const messages = defineMessages({
  couponForm: {
    defaultMessage: 'coupon form',
  },
});

interface CouponInputProps {
  drawerOrAccordionClosed: boolean;
  appliedCoupon?: AppliedCoupon;
  assignedCoupons?: AssignedCoupon[];
  doCouponCodeApply: ApplyCouponHandler;
  doCouponCodeRemove: RemoveCouponHandler;
  shippingAddress?: ShippingAddress;
}

export const CouponInput = forwardRef<HTMLInputElement, CouponInputProps>(
  (
    {
      drawerOrAccordionClosed,
      appliedCoupon,
      assignedCoupons,
      doCouponCodeApply,
      doCouponCodeRemove,
      shippingAddress,
    },
    ref
  ) => {
    const TOO_MANY_REQUESTS = 429;
    const controller = new window.AbortController();
    const intl = useIntl();
    const [couponCode, setCouponCode] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [isApplying, setIsApplying] = useState(false);
    const [isRemoving, setIsRemoving] = useState(false);

    useEffect(() => {
      if (errorMessage !== '' && couponCode === '') {
        setErrorMessage('');
      }
    }, [errorMessage, couponCode]);

    useEffect(() => {
      if (drawerOrAccordionClosed) {
        setErrorMessage('');
        setCouponCode('');
        setSuccessMessage('');
      }
    }, [drawerOrAccordionClosed]);

    const handleCouponCodeApply = async (couponCode: string) => {
      setIsApplying(true);
      setSuccessMessage('');
      setErrorMessage('');

      await doCouponCodeApply({
        input: { couponCode, shippingAddress },
        signal: controller.signal,
        onError: ({ responseError }) => {
          if (responseError?.status === TOO_MANY_REQUESTS) {
            setErrorMessage(
              intl.formatMessage(couponMessages.couponApplyRateLimitError)
            );
            return;
          }
          setErrorMessage(
            intl.formatMessage(serverErrorMessage.couponApplyServerError)
          );
        },
        onCompleted: (data) => {
          const userErrors: CouponError[] = data.errors || [];

          if (userErrors.length > 0) {
            const userError = userErrors[0];

            try {
              if (!userError.error_code || !userError.field) {
                throw new Error('Missing error_code or field');
              }

              const userErrorMessage = getUserErrorMessage({
                code: userError.error_code,
                field: userError.field,
              });

              setErrorMessage(
                intl.formatMessage(userErrorMessage, userError.error_params)
              );
            } catch {
              setErrorMessage(userError?.message || '');
            }
          } else {
            setSuccessMessage(
              intl.formatMessage(couponMessages.couponAppliedSuccessMessage)
            );
          }
        },
      }).finally(() => {
        setIsApplying(false);
      });
    };

    const handleCouponRemove = async () => {
      setIsRemoving(true);
      await doCouponCodeRemove({
        input: { shippingAddress },
        signal: controller.signal,
        onError: () => {
          setErrorMessage(
            intl.formatMessage(serverErrorMessage.couponRemoveServerError)
          );
        },
        onCompleted: () => {
          setCouponCode('');
          setSuccessMessage('');
          setErrorMessage('');
        },
      }).finally(() => {
        setIsRemoving(false);
      });
    };

    const handleCouponChange = useCallback(
      (event) => {
        setCouponCode(event.target.value);
        if (errorMessage !== '') {
          setErrorMessage('');
        }
      },
      [errorMessage]
    );

    const handleBlur = useCallback(() => {
      if (errorMessage !== '') {
        setErrorMessage('');
      }
    }, [errorMessage]);

    const couponCodeOverwrite = () => {
      if (!appliedCoupon) {
        return couponCode;
      }
      // When it is a manual type coupon, preserve the same case as typed into the input field before users close and reopen the modal
      if (successMessage !== '') {
        return couponCode;
      }
      if (
        assignedCoupons?.find(
          (assignedCoupon) =>
            assignedCoupon?.code.toUpperCase() ===
            appliedCoupon?.code.toUpperCase()
        )
      ) {
        return couponCode;
      }
      return appliedCoupon.code;
    };

    const manualCodeApplied =
      appliedCoupon?.code.toUpperCase() === couponCodeOverwrite().toUpperCase();

    const couponLoading = isApplying || isRemoving;

    const handleCouponApply = (event) => {
      event.preventDefault();
      if (couponLoading) {
        return; // already loading
      }
      if (manualCodeApplied) {
        handleCouponRemove();
      } else {
        handleCouponCodeApply(couponCode);
      }
    };

    const hasError = errorMessage?.length > 0;
    const inputIntent = !couponCode ? constants.NEUTRAL : constants.PRIMARY;

    return (
      <form
        onSubmit={handleCouponApply}
        aria-label={intl.formatMessage(messages.couponForm)}
      >
        <Box className={styles.couponInput} display="flex">
          <Input
            size={constants.SMALL}
            autoCapitalize="off"
            autoComplete="off"
            placeholder={intl.formatMessage(
              couponMessages.enterCodeInputPlaceholder
            )}
            onBlur={handleBlur}
            onChange={handleCouponChange}
            label={intl.formatMessage(couponMessages.couponCode)}
            type="text"
            name="couponCode"
            disabled={manualCodeApplied}
            aria-busy={couponLoading}
            required
            fluid
            aria-required
            aria-label={intl.formatMessage(couponMessages.couponCode)}
            aria-invalid={hasError}
            aria-describedby="coupon-code-apply-message"
            value={couponCodeOverwrite()}
            innerRef={ref}
          />
          {((successMessage && manualCodeApplied) || errorMessage) && (
            <CouponAppliedMessage
              successMessage={successMessage}
              errorMessage={errorMessage}
            />
          )}
          <Button
            type="submit"
            size={constants.MEDIUM}
            intent={manualCodeApplied ? constants.DESTRUCTIVE : inputIntent}
            fluid
            strong
            disabled={couponLoading || !couponCodeOverwrite()}
            loading={couponLoading}
          >
            {manualCodeApplied
              ? intl.formatMessage(couponMessages.removeCouponButton)
              : intl.formatMessage(couponMessages.applyCouponButton)}
          </Button>
        </Box>
      </form>
    );
  }
);
